import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import AnimatedModal from '../ui/AnimatedModal';
import { LinkIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * ConnectedScreeningsModal — connect screenings of one job post so the duplicate-candidate
 * check treats them as one hiring cycle (e.g. October assessment → October AI interview).
 * A candidate present in two connected screenings is not flagged as a duplicate; one who
 * re-applies in an unconnected screening still is. Server: screeningDuplicateGroups.route.js.
 *
 * Every write returns the job's fresh list, so the modal renders the server's state rather
 * than patching its own copy.
 */

const NEW_GROUP = 'new';

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function errorMessage(err, fallback) {
  return err?.response?.data?.message || fallback;
}

const ConnectedScreeningsModal = ({ isOpen, onClose, jobId }) => {
  const [screenings, setScreenings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState('unconnected');
  const [selected, setSelected] = useState(() => new Set());
  const [target, setTarget] = useState(NEW_GROUP);
  const [busy, setBusy] = useState(false);

  const baseUrl = `/api/screenings/job/${jobId}/duplicate-check-groups`;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await axios.get(baseUrl);
      setScreenings(res.data?.screenings || []);
    } catch (err) {
      setLoadError(errorMessage(err, 'Could not load screenings'));
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    if (isOpen && jobId) {
      setTab('unconnected');
      setSelected(new Set());
      setTarget(NEW_GROUP);
      load();
    }
  }, [isOpen, jobId, load]);

  const unconnected = useMemo(() => screenings.filter((s) => !s.groupId), [screenings]);

  // Groups in the order their newest member appears (the list arrives newest first).
  const groups = useMemo(() => {
    const byId = new Map();
    for (const s of screenings) {
      if (!s.groupId) continue;
      if (!byId.has(s.groupId)) byId.set(s.groupId, []);
      byId.get(s.groupId).push(s);
    }
    return Array.from(byId, ([groupId, members], i) => ({
      groupId,
      label: `Group ${i + 1}`,
      members,
    }));
  }, [screenings]);

  // A group chosen as the target can vanish after a write (dissolved); fall back to "new".
  useEffect(() => {
    if (target !== NEW_GROUP && !groups.some((g) => g.groupId === target)) {
      setTarget(NEW_GROUP);
    }
  }, [groups, target]);

  const minSelected = target === NEW_GROUP ? 2 : 1;
  const canConnect = !busy && selected.size >= minSelected;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConnect = async () => {
    if (!canConnect) return;
    setBusy(true);
    try {
      const body = { screeningIds: Array.from(selected) };
      if (target !== NEW_GROUP) body.groupId = target;
      const res = await axios.post(baseUrl, body);
      setScreenings(res.data?.screenings || []);
      setSelected(new Set());
      setTab('connected');
      toast.success('Screenings connected');
    } catch (err) {
      toast.error(errorMessage(err, 'Could not connect screenings'));
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async (screeningIds, successText) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${baseUrl}/disconnect`, { screeningIds });
      setScreenings(res.data?.screenings || []);
      toast.success(successText);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not disconnect'));
    } finally {
      setBusy(false);
    }
  };

  const tabButton = (key, label, count) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
        tab === key
          ? 'border-[#007BFF] text-[#007BFF]'
          : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {label}
      <span className="ml-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{count}</span>
    </button>
  );

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabelledBy="connected-screenings-title"
      backdropClassName="bg-black/50"
      contentClassName="w-full max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col max-h-[90vh]"
    >
      <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-200 shrink-0">
        <div>
          <h2 id="connected-screenings-title" className="text-lg font-semibold text-gray-900">
            Connected screenings
          </h2>
          <p className="mt-0.5 text-sm text-gray-600">
            Candidates in connected screenings are not marked as duplicates of each other.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="flex gap-2 px-6 border-b border-gray-200 shrink-0">
        {tabButton('unconnected', 'Unconnected', unconnected.length)}
        {tabButton('connected', 'Connected', groups.length)}
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        {loading ? (
          <p className="py-8 text-center text-sm text-gray-500">Loading screenings…</p>
        ) : loadError ? (
          <div className="py-8 text-center">
            <p className="text-sm text-red-600">{loadError}</p>
            <button
              type="button"
              onClick={load}
              className="mt-3 text-sm font-semibold text-[#007BFF] hover:underline"
            >
              Retry
            </button>
          </div>
        ) : tab === 'unconnected' ? (
          unconnected.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">Every screening is connected.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {unconnected.map((s) => (
                <li key={s._id}>
                  <label className="flex cursor-pointer items-center gap-3 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(s._id)}
                      onChange={() => toggle(s._id)}
                      disabled={busy}
                      className="h-4 w-4 rounded border-gray-300 text-[#007BFF] focus:ring-[#007BFF]"
                    />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900">
                      {s.name || 'Untitled screening'}
                    </span>
                    <span className="shrink-0 text-xs text-gray-500">{formatDate(s.createdAt)}</span>
                  </label>
                </li>
              ))}
            </ul>
          )
        ) : groups.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No connected screenings yet.</p>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.groupId} className="rounded-xl border border-gray-200">
                <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-2.5">
                  <span className="text-sm font-semibold text-gray-900">{g.label}</span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      handleDisconnect(
                        g.members.map((m) => m._id),
                        `${g.label} disconnected`
                      )
                    }
                    className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-50"
                  >
                    Disconnect all
                  </button>
                </div>
                <ul className="divide-y divide-gray-100">
                  {g.members.map((m) => (
                    <li key={m._id} className="flex items-center gap-3 px-4 py-2.5">
                      <span className="min-w-0 flex-1 truncate text-sm text-gray-800">
                        {m.name || 'Untitled screening'}
                      </span>
                      <span className="shrink-0 text-xs text-gray-500">{formatDate(m.createdAt)}</span>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => handleDisconnect([m._id], 'Screening disconnected')}
                        title={
                          g.members.length <= 2
                            ? 'Removing one of two screenings ends this group'
                            : 'Remove from this group'
                        }
                        className="shrink-0 text-xs font-semibold text-gray-500 hover:text-red-600 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {tab === 'unconnected' && !loading && !loadError && unconnected.length > 0 && (
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 shrink-0">
          {groups.length > 0 && (
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              disabled={busy}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30"
              aria-label="Connect to"
            >
              <option value={NEW_GROUP}>As a new group</option>
              {groups.map((g) => (
                <option key={g.groupId} value={g.groupId}>
                  Add to {g.label}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={handleConnect}
            disabled={!canConnect}
            className="flex items-center gap-2 rounded-lg bg-[#007BFF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0066d6] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <LinkIcon className="h-4 w-4" />
            {busy ? 'Connecting…' : `Connect${selected.size ? ` (${selected.size})` : ''}`}
          </button>
        </div>
      )}
    </AnimatedModal>
  );
};

export default ConnectedScreeningsModal;
